import React, { useMemo, useState } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { useSelector } from 'react-redux';

import api from "../../../../utils/api"
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import IcomoonIcon from '../../../../components/IcomoonIcon';
import Toggle from '../../../../components/Toggle';
import { useQuery } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import Header from '../../../../navigation/Header';
import useActiveResidentCommunity from '../../../../hooks/useActiveResidentCommunity';
import dayjs from 'dayjs';


const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]


function AnnouncementRow({ announcement }) {
  const navigation = useNavigation()
  const dateStr = dayjs(announcement.created_at).format('D MMM')
  const isNew = announcement.seen === false

  function goToDetail() {
    navigation.navigate('Resident.Announcement.Detail', { announcementId: announcement.uuid })
  }

  return (
    <TouchableOpacity style={tw`row items-center px-3 py-3 border-b border-gray-100`} onPress={goToDetail}>
      <View style={tw`w-3 mr-1 items-center`}>
        {
          isNew &&
            <View style={tw`w-2.5 h-2.5 rounded-full bg-red-500`}></View>
        }
      </View>

      <View style={tw`flex-1`}>
        <Text style={tw`text-secondary font-500 text-base`} numberOfLines={1}>{announcement.title}</Text>

        {
          announcement.text &&
            <Text style={tw`text-gray-400`} numberOfLines={1}>{announcement.text}</Text>
        }
      </View>

      <Text style={tw`text-gray-400 text-sm ml-2`}>{dateStr}</Text>
      <IcomoonIcon name='chevron-right' color={tw.color('secondary')} size={13} style={tw`ml-2`} />
    </TouchableOpacity>
  )
}


export default function ResidentAnnouncementsScreen({ navigation, route }) {
  const { communityId } = useActiveResidentCommunity()
  const auth = useSelector((state) => state.auth)

  // No hay endpoint dedicado de lista; usamos los comunicados del dashboard del residente
  // (misma caché que el dashboard).
  const { isLoading, data } = useQuery({
    queryKey: ['resident.community.dashboard', communityId, auth.user.email],
    queryFn: () => api.get(`/communities/${communityId}/resident/dashboard`).then(response => response.data),
  })

  const announcementsList = useMemo(() => {
    return Array.isArray(data?.announcements) ? data.announcements : []
  }, [data])

  const unreadCount = useMemo(() => {
    return announcementsList.filter(a => a.seen === false).length
  }, [announcementsList])

  const [onlyUnread, setOnlyUnread] = useState(false);

  const visibleList = useMemo(() => {
    return onlyUnread ? announcementsList.filter(a => a.seen === false) : announcementsList
  }, [announcementsList, onlyUnread])

  // Agrupar por mes (más reciente primero).
  const monthGroups = useMemo(() => {
    const groups = {}
    visibleList.forEach(a => {
      const d = dayjs(a.created_at)
      const key = d.format('YYYY-MM')
      if (!groups[key]) {
        groups[key] = { key, label: `${MONTHS[d.month()]} ${d.year()}`, items: [] }
      }
      groups[key].items.push(a)
    })
    return Object.values(groups)
      .sort((a, b) => (a.key < b.key ? 1 : -1))
      .map(g => ({ ...g, items: g.items.sort((x, y) => (x.created_at < y.created_at ? 1 : -1)) }))
  }, [visibleList])


  return (
    <View style={tw`flex-1 bg-[#F3F2F3]`}>
      <Header style={tw`bg-secondary rounded-b-0 mb-0`} showBackButton backButtonColor='white'>
        <View style={tw``}>
          <Text style={tw`text-white text-lg font-600`}>Comunicados</Text>
          {
            isLoading ?
              <Text style={tw`text-white/50`}>Cargando...</Text>
            :
              <View style={tw`row`}>
                <Text style={tw`text-white/80`}>{announcementsList.length} publicados</Text>
                <Text style={tw`text-white/40 mx-2`}>·</Text>
                <Text style={tw`text-white/80`}>{unreadCount} sin leer</Text>
              </View>
          }
        </View>
      </Header>

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`py-4 px-2 pb-20`}>
        <View style={tw`row justify-between items-center mb-4 bg-white border border-gray-200 rounded-2xl px-4 py-3`}>
          <Text style={tw`text-gray-600`}>Mostrar solo no leídos</Text>
          <Toggle
            value={onlyUnread}
            onValueChange={setOnlyUnread}
          />
        </View>

        {
          !isLoading && monthGroups.length === 0 &&
            <Text style={tw`text-gray-400 text-center mt-20`}>
              {onlyUnread ? 'No tienes comunicados sin leer' : 'Sin comunicados'}
            </Text>
        }

        {
          monthGroups.map(group =>
            <View key={group.key} style={tw`mb-6`}>
              <View style={tw`bg-gray-200 mb-4 self-center px-6 py-0.5 rounded-full border border-gray-300`}>
                <Text style={tw`text-gray-400`}>{group.label}</Text>
              </View>

              <View style={tw`bg-white border border-gray-200 rounded-2xl`}>
                {
                  group.items.map(announcement =>
                    <AnnouncementRow key={announcement.uuid} announcement={announcement} />
                  )
                }
              </View>
            </View>
          )
        }
      </ScrollView>
    </View>
  );
}
